import { useCallback, useEffect, useState } from 'react'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'

import Motion from '@/components/base/Motion'

import { opacityReveal } from './animations/opacity'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

type GalleryProps = {
  gallery: WPImage[]
  transparent?: boolean
  className?: string
}

const Gallery = ({ gallery, className, ...props }: GalleryProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true
  })
  const [scrollProgress, setScrollProgress] = useState(0)

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress * 100)
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onScroll(emblaApi)
    emblaApi
      .on('reInit', onScroll)
      .on('scroll', onScroll)
      .on('slideFocus', onScroll)
  }, [emblaApi, onScroll])

  return (
    <Motion animation={opacityReveal}>
      <section {...props} data-gallery className={cn(className)}>
        <section
          className="gallery embla w-full overflow-hidden px-[var(--gutter)]"
          style={
            {
              '--slide-spacing': '1px',
              '--slide-size': '100%'
            } as React.CSSProperties & Record<string, string>
          }
        >
          <div
            className="embla__viewport cursor-grab active:cursor-grabbing"
            ref={emblaRef}
          >
            <div className="embla__container ml-[calc(-1*var(--slide-spacing))] flex touch-pan-y touch-pinch-zoom">
              {gallery.map((media, index) => (
                <div
                  className="embla__slide min-w-0 flex-[0_0_var(--slide-size)] pl-[var(--slide-spacing)]"
                  style={
                    {
                      '--width': `${media.width}px`,
                      '--height': `${media.height}px`,
                      flexBasis:
                        'calc((var(--width) * var(--gallery-height)) / var(--height))'
                    } as React.CSSProperties & Record<string, string>
                  }
                  key={index}
                >
                  {media.url ? (
                    <Image
                      src={media.url}
                      width={media.width}
                      height={media.height}
                      alt={media.alt}
                      className="h-[var(--gallery-height)] w-auto"
                      data-overlay-image
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div
            className={cn(
              'embla__progress relative mt-[30px] h-[2px] w-full overflow-hidden',
              'bg-[#F0F2F5]'
            )}
          >
            <div
              className={cn(
                'embla__progress__bar left absolute -left-full bottom-0 top-0 w-full',
                'bg-blue-900'
              )}
              style={{ transform: `translate3d(${scrollProgress}%,0px,0px)` }}
            />
          </div>
        </section>
      </section>
    </Motion>
  )
}

export default Gallery
